import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  FiActivity,
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiCopy,
  FiCpu,
  FiCrosshair,
  FiFileText,
  FiHelpCircle,
  FiImage,
  FiLoader,
  FiLock,
  FiMapPin,
  FiMessageSquare,
  FiRepeat,
  FiSend,
  FiServer,
  FiShield,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Button } from '../../ComponnentsUI/common/Button';
import { buildReportContext, ProblemReportContext } from '../../../helpers/problemReport/reportContext';
import {
  PatientImpact,
  ProblemReportCategory,
  ProblemReportReceipt,
  useSubmitProblemReport,
} from '../../../helpers/api/problem-reports/problemReports';

const CATEGORIES: { key: ProblemReportCategory; icon: React.ElementType; color: string }[] = [
  { key: 'ai_result', icon: FiCpu, color: '#8B5CF6' },
  { key: 'measurement', icon: FiCrosshair, color: '#3B82F6' },
  { key: 'report', icon: FiFileText, color: '#10B981' },
  { key: 'images', icon: FiImage, color: '#06B6D4' },
  { key: 'patient_record', icon: FiUser, color: '#EC4899' },
  { key: 'exam_transfer', icon: FiRepeat, color: '#F59E0B' },
  { key: 'scheduling', icon: FiCalendar, color: '#14B8A6' },
  { key: 'access_privacy', icon: FiShield, color: '#EF4444' },
  { key: 'other', icon: FiHelpCircle, color: '#64748B' },
];

const IMPACTS: { key: PatientImpact; color: string }[] = [
  { key: 'yes', color: '#EF4444' },
  { key: 'unsure', color: '#F59E0B' },
  { key: 'no', color: '#10B981' },
];

const MIN_DESCRIPTION = 10;
const MAX_DESCRIPTION = 4000;
const FOCUS_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

type Props = { isOpen: boolean; onClose: () => void };

function SectionTitle({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <h3 className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-title-page-color-100 dark:text-white">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold text-white">{n}</span>
      {children}
    </h3>
  );
}

export default function ProblemReportDialog({ isOpen, onClose }: Props) {
  const { t } = useTranslation('ProblemReport');
  const location = useLocation();
  const submit = useSubmitProblemReport();
  const [category, setCategory] = useState<ProblemReportCategory | null>(null);
  const [impact, setImpact] = useState<PatientImpact | null>(null);
  const [description, setDescription] = useState('');
  const [context, setContext] = useState<ProblemReportContext | null>(null);
  const [receipt, setReceipt] = useState<ProblemReportReceipt | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setContext(buildReportContext(location.pathname, location.search));
    setCategory(null);
    setImpact(null);
    setDescription('');
    setReceipt(null);
    setShowContext(false);
    setCopied(false);
    submit.reset();
  }, [isOpen]);

  const trimmedLength = description.trim().length;
  const canSend = !!category && !!impact && trimmedLength >= MIN_DESCRIPTION && !submit.isPending;
  const flagged = impact === 'yes' || impact === 'unsure';
  const failedCount = context?.failed_requests.length ?? 0;

  const contextRows = useMemo(() => {
    if (!context) return [];
    return [
      [t('ctxVersion'), [context.app_version, context.build].filter(Boolean).join(' / ')],
      [t('ctxPage'), context.page_path],
      ...(context.study_instance_uid ? [[t('ctxExam'), context.study_instance_uid]] : []),
      ...(context.series_instance_uid ? [[t('ctxSeries'), context.series_instance_uid]] : []),
      ...(context.viewer_tab ? [[t('ctxTab'), [context.viewer_tab, context.report_section].filter(Boolean).join(' / ')]] : []),
      [
        t('ctxFailedRequests'),
        failedCount
          ? context.failed_requests
              .map(f => `${f.method} ${f.path}  ${f.status || t('ctxNoResponse')}${f.error_id ? `  #${f.error_id.slice(0, 8)}` : ''}`)
              .join('\n')
          : t('ctxNone'),
      ],
      ...(context.error_tracker_event_id ? [[t('ctxErrorId'), context.error_tracker_event_id]] : []),
    ];
  }, [context, failedCount, t]);

  const send = () => {
    if (!canSend || !context) return;
    submit.mutate(
      { surface: 'web', category, patient_impact: impact, description: description.trim(), context },
      { onSuccess: setReceipt }
    );
  };

  const copyReference = async () => {
    if (!receipt) return;
    await navigator.clipboard.writeText(receipt.reference);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (receipt) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md" fitContent>
        <div className="relative px-8 pb-7 pt-9 text-center">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className={cn('absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 transition hover:bg-card-color-200 hover:text-gray-600', FOCUS_RING)}
          >
            <FiX size={18} />
          </button>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: '#10B9811A', color: '#10B981' }}>
            <FiCheckCircle size={28} />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-title-page-color-100 dark:text-white">{t('sentTitle')}</h2>
          <button
            type="button"
            onClick={copyReference}
            title={t('copyReference')}
            className={cn(
              'mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-card-color-200 px-3 py-1.5 font-mono text-sm font-semibold text-title-page-color-100 transition hover:bg-card-color-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white',
              FOCUS_RING
            )}
          >
            {receipt.reference}
            {copied ? <FiCheck size={14} className="text-emerald-600" /> : <FiCopy size={14} className="text-gray-400" />}
          </button>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-gray-500 dark:text-gray-400">{t('sentFollowUp')}</p>
          <Button onClick={onClose} className="mt-6 w-full">
            {t('close')}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" labelledBy="problem-report-title">
      <div className="flex max-h-[90vh] flex-col">
        <header className="flex items-center gap-3.5 px-6 pb-4 pt-5">
          <div className="rounded-full p-2.5" style={{ backgroundColor: '#3B82F61A', color: '#3B82F6' }}>
            <FiMessageSquare size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="problem-report-title" className="text-base font-semibold text-title-page-color-100 dark:text-white">
              {t('title')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cancel')}
            className={cn('rounded-lg p-1.5 text-gray-400 transition hover:bg-card-color-200 hover:text-gray-600', FOCUS_RING)}
          >
            <FiX size={18} />
          </button>
        </header>

        {context && (
          <div className="mx-6 rounded-xl border border-gray-200 bg-card-color-200 px-3.5 py-2.5 dark:border-gray-700 dark:bg-gray-900/50">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
              <span className="font-medium text-gray-500 dark:text-gray-400">{t('attachedLabel')}</span>
              <span className="inline-flex items-center gap-1.5 font-medium text-title-page-color-100 dark:text-gray-200">
                {context.study_instance_uid ? <FiActivity size={13} className="text-blue-600" /> : <FiMapPin size={13} className="text-blue-600" />}
                {context.study_instance_uid
                  ? [t('chipExam'), context.viewer_tab !== 'generalViewer' && context.viewer_tab, context.report_section]
                      .filter(Boolean)
                      .join(' › ')
                  : context.page_path}
              </span>
              {failedCount > 0 && (
                <span className="inline-flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-300">
                  <FiServer size={13} />
                  {t('chipErrors', { count: failedCount })}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <FiLock size={12} />
                {t('footerNote', { version: context.app_version })}
              </span>
              <button
                type="button"
                onClick={() => setShowContext(v => !v)}
                className={cn('ml-auto inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400', FOCUS_RING)}
              >
                {t('contextSummary')}
                <FiChevronDown size={13} className={cn('transition-transform', showContext && 'rotate-180')} />
              </button>
            </div>
            {showContext && (
              <dl className="mt-2.5 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 border-t border-gray-200 pt-2.5 text-[11px] dark:border-gray-700">
                {contextRows.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                    <dd className="whitespace-pre-wrap break-all font-mono text-title-page-color-100 dark:text-gray-200">{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </div>
        )}

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <section>
            <SectionTitle n={1}>{t('categoryQuestion')}</SectionTitle>
            <div role="radiogroup" className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {CATEGORIES.map(({ key, icon: Icon, color }) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={t(`category.${key}.hint`)}
                    onClick={() => setCategory(key)}
                    className={cn(
                      'relative flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition',
                      FOCUS_RING,
                      active
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500 dark:bg-blue-950/40'
                        : 'border-gray-200 bg-card-color-100 hover:border-gray-300 hover:bg-card-color-200 dark:border-gray-700 dark:bg-gray-800'
                    )}
                  >
                    <span className="shrink-0 rounded-full p-2" style={{ backgroundColor: `${color}1A`, color }}>
                      <Icon size={15} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-title-page-color-100 dark:text-white">
                      {t(`category.${key}.label`)}
                    </span>
                    {active && <FiCheck size={15} className="shrink-0 text-blue-600" />}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 h-4 text-xs text-gray-500 dark:text-gray-400">{category ? t(`category.${category}.hint`) : ''}</p>
          </section>

          <section>
            <SectionTitle n={2}>{t('impactQuestion')}</SectionTitle>
            <div role="radiogroup" className="inline-flex rounded-lg border border-gray-200 bg-card-color-200 p-1 dark:border-gray-700 dark:bg-gray-800">
              {IMPACTS.map(({ key, color }) => {
                const active = impact === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setImpact(key)}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition',
                      FOCUS_RING,
                      active ? 'bg-white text-title-page-color-100 shadow-sm dark:bg-gray-700 dark:text-white' : 'text-gray-500 hover:text-title-page-color-100 dark:text-gray-400'
                    )}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? color : '#CBD5E1' }} />
                    {t(`impact.${key}`)}
                  </button>
                );
              })}
            </div>
            {flagged && (
              <p className="mt-2.5 flex items-center gap-2 text-xs font-medium text-red-600 dark:text-red-400">
                <FiAlertTriangle size={13} className="shrink-0" />
                {t('flaggedNote')}
              </p>
            )}
          </section>

          <section>
            <SectionTitle n={3}>{t('descriptionLabel')}</SectionTitle>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION}
              rows={3}
              placeholder={t('descriptionPlaceholder')}
              className="block w-full resize-none rounded-xl border border-gray-200 bg-card-color-100 px-3.5 py-2.5 text-sm text-title-page-color-100 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
            <div className="mt-1.5 flex items-center justify-between gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <FiLock size={11} />
                {t('phiWarning')}
              </span>
              <span className={cn('shrink-0 tabular-nums', trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION ? 'text-amber-600' : 'text-gray-400')}>
                {description.length} / {MAX_DESCRIPTION}
              </span>
            </div>
          </section>

          {submit.isError && (
            <p role="alert" className="flex items-center gap-2 rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200">
              <FiAlertTriangle size={15} className="shrink-0" />
              {t('sendFailed')}
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3.5 dark:border-gray-700">
          <Button variant="ghost" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button onClick={send} disabled={!canSend} className="gap-2">
            {submit.isPending ? <FiLoader size={15} className="animate-spin" /> : <FiSend size={15} />}
            {submit.isPending ? t('sending') : t('send')}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}
